import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import {
  ScheduledJobProto,
  ScheduledJobType,
} from "build-ts/schedule";
import { Header } from "build-ts/metadata";
import {
  getScheduledJobRunsByJobId,
  deleteScheduledJobRun,
} from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import JobRunCard from "./JobRunCard";

interface JobRunsProps {
  scheduledJob: ScheduledJobProto;
  scheduleJobRuns: Header[];
  onRunDeleted?: (runId: string) => void;
  onBack?: () => void;
}

// Main JobRuns component
const JobRuns: React.FC<JobRunsProps> = ({ scheduledJob, scheduleJobRuns, onRunDeleted }) => {
  const [jobRuns, setJobRuns] = useState<Header[]>([]);
  const [isLoading, setIsLoading] = useState(true);


  // Load job runs
  useEffect(() => {
    if (scheduleJobRuns) {
      setJobRuns([...scheduleJobRuns]);
      setIsLoading(false);
    } else {
      // If job_runs is not populated, fetch them
      loadJobRuns();
    }
  }, [scheduledJob]);

  const loadJobRuns = async () => {
    if (!scheduledJob.header?.id) return;

    setIsLoading(true);
    const runs = await getScheduledJobRunsByJobId(scheduledJob?.header?.id);
    if (runs) {
      setJobRuns(runs);
    }
    setIsLoading(false);
  };



  const handleDeleteRun = async (runId: string) => {
    const result = await deleteScheduledJobRun(runId);
    if (result) {
      setJobRuns((prev) => prev.filter((run) => run.id !== runId));
      showToast("Job run deleted successfully", "", "success");
      onRunDeleted?.(runId);
    }
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" />
        <Text mt={4}>Loading job runs...</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={2} align="stretch" h="100%">
      {/* Job Runs List */}
      {jobRuns.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No job runs found for this scheduled job.
        </Alert>
      ) : (
        <VStack spacing={1} align="stretch" h="100%">
          <HStack justify="space-between" align="center" flexShrink={0}>
            <Text fontSize="sm" color="fg.default" mb={0} ml={1} fontWeight="700">
              Total runs: {jobRuns.length}
            </Text>
          </HStack>

          <Box
            flex={1}
            overflowY="auto"
            maxH="680px"
            pr={2}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a8a8a8",
              },
            }}
          >
            <VStack spacing={1} align="stretch">
              {jobRuns?.map((jobRun) => (
                <JobRunCard
                  scheduledJob={scheduledJob}
                  key={jobRun.id}
                  jobRun={jobRun}
                  scheduledJobType={
                    scheduledJob.type || ScheduledJobType.PLAN_EXECUTION
                  }
                  onDelete={() => {
                    handleDeleteRun(jobRun?.id || "");
                  }}
                />
              ))}
            </VStack>
          </Box>
        </VStack>
      )}
    </VStack>
  );
};

export default JobRuns;
